import React from 'react';
import { Card, CardContent, Typography, List, ListItem, ListItemIcon, ListItemText, Chip, Box } from '@mui/material';
import {
    WarningAmber as WarningIcon,
    AccessTime as PendingIcon,
    CheckCircle as OkIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface AlertsFeedProps {
    pendingOrders: number;
    lowStockCount: number;
    loading?: boolean;
}

const AlertsFeed: React.FC<AlertsFeedProps> = ({ pendingOrders, lowStockCount, loading }) => {
    const navigate = useNavigate();

    if (loading) return <Card sx={{ height: '100%' }}><CardContent><Typography>Loading updates...</Typography></CardContent></Card>;

    const hasAlerts = pendingOrders > 0 || lowStockCount > 0;

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: '16px !important' }}> {/* Compact padding */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                        Attention Required
                    </Typography>
                    {!hasAlerts && <Chip label="All Clear" color="success" size="small" icon={<OkIcon />} />}
                </Box>

                <List dense disablePadding>
                    {pendingOrders > 0 && (
                        <ListItem
                            button
                            onClick={() => navigate('/orders')}
                            sx={{
                                bgcolor: 'warning.main',
                                bgOpacity: 0.1, // Note: bgOpacity isn't standard MUI, using style below
                                borderRadius: 1,
                                mb: 1,
                                backgroundColor: 'rgba(237, 108, 2, 0.08)'
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <PendingIcon color="warning" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={`${pendingOrders} Orders Pending`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'warning.main' }}
                            />
                        </ListItem>
                    )}

                    {lowStockCount > 0 && (
                        <ListItem
                            button
                            onClick={() => navigate('/products')} // Ideally filter by low stock
                            sx={{
                                borderRadius: 1,
                                backgroundColor: 'rgba(211, 47, 47, 0.08)'
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <WarningIcon color="error" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={`${lowStockCount} Products Low Stock`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'error.main' }}
                            />
                        </ListItem>
                    )}

                    {!hasAlerts && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                            No urgent items requiring attention.
                        </Typography>
                    )}
                </List>
            </CardContent>
        </Card>
    );
};

export default AlertsFeed;
